import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Shield, Download, Eye, EyeOff } from 'lucide-react';
import InstallPWAButton from '@/components/InstallPWAButton';
import usePWAStandalone from '@/hooks/usePWAStandalone';

const LOGO = 'https://customer-assets.emergentagent.com/job_audit-ia-1/artifacts/bwe3ss1d_1.png';
const ACCENT = '#00875A';

/**
 * Punto de entrada EXCLUSIVO para la app del inspector.
 * - Ruta: /app
 * - Solo acepta usuarios con rol `inspector`.
 * - Otros roles reciben mensaje explícito y son enviados al login normal.
 * - Es la ruta configurada como start_url del manifest PWA.
 */
export default function InspectorApp() {
    const { user, loading, login, logout } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const isStandalone = usePWAStandalone();
    const navigate = useNavigate();

    // Auto-redirigir si ya está autenticado como auditor
    useEffect(() => {
        if (!loading && user?.role === 'inspector') {
            navigate('/inspector', { replace: true });
        }
    }, [user, loading, navigate]);

    // Si está logueado con otro rol, forzar logout con aviso
    useEffect(() => {
        if (!loading && user && user.role !== 'inspector') {
            toast.error('Esta app es exclusiva para auditores. Cerrando sesión...');
            logout();
            navigate('/app', { replace: true });
        }
    }, [user, loading, logout, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) return;
        setSubmitting(true);
        try {
            const u = await login(email, password);
            if (u?.role !== 'inspector') {
                toast.error('Esta app es exclusiva para auditores. Usa la plataforma web para tu rol.');
                await logout();
                navigate('/app', { replace: true });
                return;
            }
            navigate('/inspector', { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Credenciales inválidas');
        } finally { setSubmitting(false); }
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0A0A' }}>
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: ACCENT }} />
        </div>
    );

    return (
        <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#0A0A0A', color: 'white' }}>
            <div className="max-w-md w-full">
                {/* Header con logo */}
                <div className="text-center mb-8">
                    <img src={LOGO} alt="AIGIS" className="h-20 w-20 mx-auto object-contain mb-4" />
                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">AIGIS CERTIFICATE</div>
                    <h1 className="font-display font-black text-3xl mt-1">Auditor App</h1>
                    <div className="text-xs opacity-60 mt-1">Acceso exclusivo para auditores certificados</div>
                </div>

                {/* Login form */}
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="inspector-app-login">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold opacity-60 block mb-1">Correo</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                            required autoComplete="username"
                            data-testid="inspector-app-email"
                            className="w-full px-3 py-3 bg-white/5 border border-white/10 text-white focus:border-white/40 outline-none" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold opacity-60 block mb-1">Contraseña</label>
                        <div className="relative">
                            <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                                required autoComplete="current-password"
                                data-testid="inspector-app-password"
                                className="w-full px-3 py-3 pr-10 bg-white/5 border border-white/10 text-white focus:border-white/40 outline-none" />
                            <button type="button" onClick={() => setShowPw((v) => !v)}
                                aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                title={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                data-testid="inspector-app-toggle-password"
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-white/50 hover:text-white focus:outline-none">
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                    </div>
                    <button type="submit" disabled={submitting}
                        data-testid="inspector-app-submit"
                        className="w-full py-3 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        style={{ background: ACCENT }}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                        Iniciar sesión
                    </button>
                </form>

                {/* Install banner - solo si NO está instalada */}
                {!isStandalone && (
                    <div className="mt-8 p-4 border" style={{ background: 'rgba(0,135,90,0.06)', borderColor: 'rgba(0,135,90,0.30)' }}>
                        <div className="flex items-center gap-2 mb-2">
                            <Download className="w-4 h-4" style={{ color: ACCENT }} />
                            <div className="font-bold text-sm">¿Trabajas en campo con tablet?</div>
                        </div>
                        <div className="text-xs opacity-80 mb-3">
                            Instala AIGIS Auditor como app nativa. Tendrás ícono en tu pantalla de inicio y no verás las barras del navegador.
                        </div>
                        <InstallPWAButton />
                    </div>
                )}

                {/* Footer */}
                <div className="mt-8 text-center text-[10px] uppercase tracking-widest font-bold opacity-40">
                    AIGIS · Safety Solutions
                </div>
            </div>
        </div>
    );
}
